/**
 * What the company has been worth, on a real time axis — decision 57.
 *
 * The chart this replaced was one bar per round in card order, which said only
 * "these rounds happened, in this sequence". The twenty months between two
 * rounds looked identical to the three weeks between two others, and the money
 * raised was the only quantity on the screen.
 *
 * ## Value is an observation, not a line
 *
 * A company is priced on the day a round closes and on no other day. So the
 * dots are the only real figures here: a **hollow** dot for pre-money, a
 * **filled** dot for post-money, and the stem between them is what the round
 * added. Between rounds the line is **stippled** — the last observed value
 * carried forward, which is not the same claim as a valuation, and the legend
 * says so in those words.
 *
 * Where a round opens at a different price than the previous one closed at, the
 * gap is drawn rather than smoothed away. That step is a repricing, and a line
 * sloping neatly between two rounds would invent a month-by-month drift that
 * nobody measured.
 *
 * ## The raise gets its own band and its own scale
 *
 * A raise is two orders of magnitude smaller than a valuation. On one axis the
 * bars would be hairlines, and scaling them to be visible would put a 3 MNOK
 * round level with a 200 MNOK valuation. So they sit beneath, labelled, on a
 * scale of their own — decision 57 asks for exactly this, and it is the only
 * honest way to show both.
 *
 * ## Shares outstanding, not shares issued
 *
 * Value is shares outstanding × the round's price. Buy-backs lower outstanding,
 * so they lower the next pre-money; paid option exercises booked against a
 * round count as shares it issued. Both follow from `holdings()` rather than
 * being special-cased here.
 */
import { useMemo } from 'react'
import { fmtKrPrice, fmtKrSum } from '@/shared/util/money'
import { formatDate, todayIso } from '@/shared/util/dates'
import { issuanceTotals, type Issuance, type Shareholder, type ShareTransfer } from '@/types/ownership'
import './CapitalTimeline.css'

const DAY = 86_400_000
const ms = (iso: string) => Date.parse(`${iso}T00:00:00`)
const nb = (n: number) => n.toLocaleString('nb-NO')

export function CapitalTimeline({
  issuances,
  holders,
  transfers,
}: {
  /** Oldest first — decision 18 sorts on read, and this relies on it. */
  issuances: Issuance[]
  holders: Shareholder[]
  transfers: ShareTransfer[]
}) {
  const model = useMemo(() => {
    /**
     * What counts as an observation of value — decision 76, and F1 + F3.
     *
     * A dot here is *shares actually outstanding × a price somebody actually
     * paid*. Two kinds of round fail that and both were being drawn:
     *
     *  - **An open round** has issued nothing by definition, so it has no
     *    post-money and a dot at its price would be a valuation nobody made.
     *    76: it joins the chart the day it closes.
     *  - **A round that issued nothing**, open or not. The 24 Aug run created
     *    `ZZTEST Emisjon` at kr 10,00 with no shares against it and the
     *    company's headline value fell from kr 32,2M to kr 485k — the arithmetic
     *    was right (49 902 × kr 10) and the figure was nonsense, because nobody
     *    paid kr 10 for anything. A price nobody paid is not a valuation.
     *
     * Excluding them cannot disturb the running totals: both contribute nought
     * issued and nought raised, so `cumulative` and `running` are unchanged.
     */
    const datedAll = issuances.filter((i) => i.date && Number.isFinite(ms(i.date)))
    const dated = datedAll.filter(
      (i) => i.status !== 'ongoing' && issuanceTotals(i, holders).shares > 0,
    )
    /** Named under the chart, so a round missing from it is never a silent gap. */
    const notPriced = datedAll.filter((i) => !dated.includes(i))
    if (dated.length === 0) return null

    /** Shares the company has bought back and holds, before a given date. */
    const buyBacksBefore = (iso: string) =>
      transfers.reduce((n, t) => n + (t.settled && t.buyback && t.date < iso ? t.shares : 0), 0)

    let cumulative = 0
    let running = 0
    const rounds = dated.map((iss) => {
      const totals = issuanceTotals(iss, holders)
      const issued = totals.shares
      const raised = totals.raised ?? 0
      const sharesBefore = cumulative - buyBacksBefore(iss.date)
      cumulative += issued
      running += raised
      const sharesAfter = sharesBefore + issued
      const price = iss.pricePerShare ?? 0
      return {
        id: iss.id,
        name: iss.name,
        date: iss.date,
        t: ms(iss.date),
        price,
        issued,
        raised,
        sharesBefore,
        sharesAfter,
        preMoney: sharesBefore * price,
        postMoney: sharesAfter * price,
        running,
      }
    })

    const today = ms(todayIso())
    const first = rounds[0]!.t
    const last = Math.max(rounds[rounds.length - 1]!.t, today)
    // A single round would otherwise have a zero-width axis.
    const span = Math.max(last - first, 90 * DAY)
    const pad = span * 0.08
    const axisMin = first - pad
    const axisMax = last + pad
    const xOf = (t: number) => ((t - axisMin) / (axisMax - axisMin)) * 100

    const vMax = Math.max(...rounds.map((r) => r.postMoney), 1) * 1.15
    const yOf = (v: number) => (1 - v / vMax) * 100
    const maxRaised = Math.max(...rounds.map((r) => r.raised), 1)

    const points = rounds.map((r) => {
      const preTop = yOf(r.preMoney)
      const postTop = yOf(r.postMoney)
      return {
        ...r,
        xPct: xOf(r.t),
        preTop,
        postTop,
        stemTop: Math.min(preTop, postTop),
        stemHeight: Math.abs(preTop - postTop),
        // A first round has nothing before it, and a pre-money of kr 0 drawn as
        // a dot on the floor reads as a measurement rather than an absence.
        showPre: r.sharesBefore > 0,
        barPct: Math.max(2, (r.raised / maxRaised) * 100),
      }
    })

    /** The stippled carry, and the step where a round reprices the last one. */
    const carries: { leftPct: number; widthPct: number; topPct: number }[] = []
    const repricings: { leftPct: number; topPct: number; heightPct: number }[] = []
    rounds.forEach((r, i) => {
      const nextT = i + 1 < rounds.length ? rounds[i + 1]!.t : Math.max(today, r.t)
      const left = xOf(r.t)
      const right = xOf(nextT)
      if (right > left) carries.push({ leftPct: left, widthPct: right - left, topPct: yOf(r.postMoney) })
      if (i + 1 < rounds.length) {
        const a = yOf(r.postMoney)
        const b = yOf(rounds[i + 1]!.preMoney)
        if (Math.abs(a - b) > 0.4) {
          repricings.push({ leftPct: right, topPct: Math.min(a, b), heightPct: Math.abs(a - b) })
        }
      }
    })

    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
      topPct: (1 - f) * 100,
      label: fmtKrSum(Math.round(vMax * f)),
    }))

    const xTicks: { leftPct: number; label: string }[] = []
    for (let y = new Date(axisMin).getFullYear(); y <= new Date(axisMax).getFullYear(); y++) {
      const t = new Date(y, 0, 1).getTime()
      if (t >= axisMin && t <= axisMax) xTicks.push({ leftPct: xOf(t), label: String(y) })
    }

    const latest = rounds[rounds.length - 1]!
    return {
      points,
      carries,
      repricings,
      yTicks,
      xTicks,
      todayPct: xOf(today),
      total: running,
      count: rounds.length,
      latest,
      maxRaised,
      // Against `datedAll`, not `dated` — a round left off for having issued
      // nothing is a different fact from one that cannot be placed on the axis,
      // and it gets its own sentence below.
      undated: issuances.length - datedAll.length,
      notPriced: notPriced.map((i) => i.name),
    }
  }, [issuances, holders, transfers])

  if (!model) return null

  return (
    <section className="captl">
      <div className="captl__head">
        <div className="card-label">Capital and value over time</div>
        <div className="captl__latest">
          <span className="captl__latestvalue">{fmtKrSum(model.latest.postMoney)}</span>
          <span className="captl__latestlabel">
            Last observed value — post-money at {model.latest.name}, {formatDate(model.latest.date)}
          </span>
        </div>
      </div>

      <div className="captl__legend">
        <span>
          <i className="captl__key captl__key--pre" /> Pre-money
        </span>
        <span>
          <i className="captl__key captl__key--post" /> Post-money
        </span>
        <span>
          <i className="captl__key captl__key--carry" /> Carried between rounds — not a valuation
        </span>
        <span>
          <i className="captl__key captl__key--bar" /> Raised in the round
        </span>
      </div>

      <div className="captl__plot">
        <div className="captl__yaxis">
          {model.yTicks.map((tick) => (
            <span className="captl__ytick" style={{ top: `${tick.topPct}%` }} key={tick.label}>
              {tick.label}
            </span>
          ))}
        </div>

        <div className="captl__grid">
          {model.yTicks.map((tick) => (
            <span className="captl__gridline" style={{ top: `${tick.topPct}%` }} key={tick.label} />
          ))}

          {model.xTicks.map((tick) => (
            <span className="captl__xtick" style={{ left: `${tick.leftPct}%` }} key={tick.label}>
              {tick.label}
            </span>
          ))}

          <span className="captl__today" style={{ left: `${model.todayPct}%` }} title="Today" />

          {model.carries.map((c, i) => (
            <span
              className="captl__carry"
              key={i}
              style={{ left: `${c.leftPct}%`, width: `${c.widthPct}%`, top: `${c.topPct}%` }}
            />
          ))}

          {model.repricings.map((r, i) => (
            <span
              className="captl__reprice"
              key={i}
              style={{ left: `${r.leftPct}%`, top: `${r.topPct}%`, height: `${r.heightPct}%` }}
              title="Repriced: the next round opened at a different value than the last one closed at"
            />
          ))}

          {model.points.map((p) => (
            <span className="captl__point" key={p.id} style={{ left: `${p.xPct}%` }}>
              <span
                className="captl__stem"
                style={{ top: `${p.stemTop}%`, height: `${p.stemHeight}%` }}
              />
              {p.showPre && (
                <span
                  className="captl__dot captl__dot--pre"
                  style={{ top: `${p.preTop}%` }}
                  title={`${p.name} — pre-money ${fmtKrSum(p.preMoney)} · ${nb(p.sharesBefore)} shares`}
                />
              )}
              <span
                className="captl__dot captl__dot--post"
                style={{ top: `${p.postTop}%` }}
                title={`${p.name} · ${formatDate(p.date)} · ${fmtKrPrice(p.price)}/share · post-money ${fmtKrSum(p.postMoney)} · ${nb(p.sharesAfter)} shares`}
              />
            </span>
          ))}
        </div>
      </div>

      {/* The raise, in its own band and on its own scale — decision 57. */}
      <div className="captl__plot captl__plot--raise">
        <div className="captl__yaxis captl__yaxis--raise">
          <span className="captl__ytick" style={{ top: '0%' }}>
            {fmtKrSum(model.maxRaised)}
          </span>
          <span className="captl__ytick captl__ytick--foot">Raised</span>
        </div>
        <div className="captl__grid captl__grid--raise">
          {model.points.map((p) => (
            <span
              className="captl__bar"
              key={p.id}
              style={{ left: `${p.xPct}%`, height: `${p.barPct}%` }}
              title={`${p.name} — raised ${fmtKrSum(p.raised)}`}
            />
          ))}
          {model.xTicks.map((tick) => (
            <span className="captl__xtick" style={{ left: `${tick.leftPct}%` }} key={tick.label}>
              {tick.label}
            </span>
          ))}
        </div>
      </div>

      <p className="micro captl__note">
        The horizontal axis is real time, so the gap between rounds is the gap that happened. Value
        is shares outstanding × the round&rsquo;s price: the hollow dot is <strong>pre-money</strong>,
        the filled dot <strong>post-money</strong>, and the stem between them is what the round
        added. Between rounds the line is stippled — that is the last observed value carried
        forward, not a valuation, because the company was not priced on those days. Paid option
        exercises booked against an increase count as shares issued by it; buy-backs reduce shares
        outstanding and so lower the next pre-money. Total raised to date {fmtKrSum(model.total)}{' '}
        across {model.count} increase{model.count === 1 ? '' : 's'}.
        {model.undated > 0 &&
          ` ${model.undated} increase${model.undated === 1 ? ' carries' : 's carry'} no date and cannot be placed on the axis.`}
        {/* Decision 76: an open round is not on this chart, and the stippled
            carry running past it to Today is the truth — the company has not
            been priced since the last round that issued anything. Named rather
            than silently dropped, so nobody wonders where their round went. */}
        {model.notPriced.length > 0 &&
          ` ${model.notPriced.join(', ')} ${model.notPriced.length === 1 ? 'is' : 'are'} not on the chart: nothing has been issued in ${model.notPriced.length === 1 ? 'it' : 'them'} yet, so there is no price anybody has paid to draw. ${model.notPriced.length === 1 ? 'It joins' : 'They join'} the chart on the day shares are issued.`}
      </p>
    </section>
  )
}
